"use client"

import { useContext, useEffect, useMemo, useRef, useState } from "react"
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Divider,
  Autocomplete,
  Snackbar,
  Alert,
  Slide,
  CircularProgress,
  InputAdornment,
  alpha,
  useTheme,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Link,
} from "@mui/material"
import AddTaskIcon from "@mui/icons-material/AddTask"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import InventoryIcon from "@mui/icons-material/Inventory"
import PlaceIcon from "@mui/icons-material/Place"
import EventIcon from "@mui/icons-material/Event"
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered"
import NotesIcon from "@mui/icons-material/Notes"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"
import { ErpContext } from "../../context/ErpContext.js"
import { getLocaleConfig, formatInputWithSeparators, parseLocalizedNumber } from "../../utils/numeros.js"
import ServiciosList from "./serviciosList.js"

function SlideTransition(props) {
  return <Slide {...props} direction="up" />
}

const mapServicio = (row) => {
  const idEntidad = Number.isFinite(Number(row?.idEntidad)) ? Number(row.idEntidad) : 2
  const cantidad = Number.isFinite(Number(row?.cantidad)) ? Number(row.cantidad) : 1
  const montoTotal = Number.isFinite(Number(row?.monto)) ? Number(row.monto) : 0
  const montoUnitario = Number.isFinite(Number(row?.montoUnitario))
    ? Number(row.montoUnitario)
    : Number.isFinite(Number(row?.precioProveedor))
    ? Number(row.precioProveedor)
    : cantidad > 0
    ? montoTotal / cantidad
    : montoTotal
  const idItem = Number.isFinite(Number(row?.idItem))
    ? Number(row.idItem)
    : Number.isFinite(Number(row?.idMateriaPrima))
    ? Number(row.idMateriaPrima)
    : null
  const descripcionServicio =
    row?.descripcion ||
    row?.materiaPrima?.descripcion ||
    row?.codigo ||
    row?.materiaPrima?.codigo ||
    `Servicio ${idItem || ""}`.trim()
  const proveedorNombre =
    row?.proveedor ||
    row?.proveedorDescripcion ||
    (row?.entidad?.descripcion && row?.entidad?.apellido
      ? `${row.entidad.descripcion} ${row.entidad.apellido}`.trim()
      : row?.entidad?.descripcion ||
        (idEntidad ? `Proveedor ${idEntidad}` : "Proveedor"))

  return {
    idItem,
    idEntidad,
    montoUnitario,
    descripcionServicio,
    proveedorNombre,
    label: `${descripcionServicio} - ${proveedorNombre}`,
    key: `${Number(idItem)}-${idEntidad}`,
  }
}

const uniqueAndSortServicios = (rows) => {
  const normalizados = (Array.isArray(rows) ? rows : [])
    .map(mapServicio)
    .filter((servicio) => Number.isFinite(servicio.idItem) && servicio.idItem > 0)

  const uniqueByKey = new Map()
  normalizados.forEach((servicio) => {
    if (!uniqueByKey.has(servicio.key)) {
      uniqueByKey.set(servicio.key, servicio)
    }
  })

  return Array.from(uniqueByKey.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "es", { sensitivity: "base" })
  )
}

const MAX_ARCHIVOS_ADJUNTO = 5
const MAX_BYTES_ARCHIVO = 2 * 1024 * 1024

const inicioDiaLocalMs = (d) => {
  if (!d) return null
  const x = new Date(d)
  return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
}

const fechaFinEsAnteriorAlInicio = (fin, inicio) => {
  if (!fin || !inicio) return false
  return inicioDiaLocalMs(fin) < inicioDiaLocalMs(inicio)
}

const parseArchivosAdjuntoDesdeItem = (raw) => {
  if (raw == null || raw === "") return []
  try {
    const arr =
      typeof raw === "string" ? JSON.parse(raw) : Array.isArray(raw) ? raw : []
    if (!Array.isArray(arr)) return []
    return arr
      .map((a, idx) => ({
        id: `adj-${idx}-${a?.nombre || idx}`,
        nombre: String(a?.nombre || "archivo"),
        mimeType: String(a?.mimeType || "application/octet-stream"),
        data: String(a?.data || ""),
      }))
      .filter((x) => x.data.length > 0)
  } catch {
    return []
  }
}

const readFileAsBase64DataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const r = reader.result
      if (typeof r !== "string") {
        reject(new Error("lectura inválida"))
        return
      }
      resolve(r)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

const parseDateValue = (value) => {
  if (!value) return null

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value
  }

  const direct = new Date(value)
  if (!Number.isNaN(direct.getTime())) {
    return direct
  }

  if (typeof value === "string" && value.includes("/")) {
    const [dd, mm, yyyy] = value.split("/")
    const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
    if (!Number.isNaN(parsed.getTime())) {
      return parsed
    }
  }

  return null
}

const parseBooleanParametro = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "TRUE"

export default function CreateGrupoPaqueteComponent({
  onClose,
  onBaseItemChange = null,
  mode = "create",
  paqueteInicial = null,
  nochesCalculoAutomaticoInicial = null,
}) {
  const isEditMode = mode === "edit"
  const theme = useTheme()
  const {
    getUbicacion,
    ubicaciones,
    getItemAtributoNoEliminados,
    itemAtributoNoEliminados,
    getItemAndItemUbicacionServicios,
    crearGrupoPaqueteApi,
    crearRecetaApi,
    getItemById,
    getBomByIdItem,
    updateItem,
    updateItemUbicaciones,
    formatoNumericoGlobal,
  } = useContext(ErpContext)

  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  const [codigoGrupo, setCodigoGrupo] = useState("")
  const [atributosForm, setAtributosForm] = useState([])
  const [fechaInicio, setFechaInicio] = useState(new Date())
  const [noches, setNoches] = useState("0")
  const [fechaFin, setFechaFin] = useState(null)
  const [grupoConfirmado, setGrupoConfirmado] = useState(() => mode === "edit")
  const [destinoSeleccionado, setDestinoSeleccionado] = useState(null)
  const [butacas, setButacas] = useState("")
  const [precioPaquete, setPrecioPaquete] = useState("")
  const [precioPaqueteDisplay, setPrecioPaqueteDisplay] = useState("")
  const [serviciosDisponibles, setServiciosDisponibles] = useState([])
  const [serviciosAsignados, setServiciosAsignados] = useState([])
  const [observaciones, setObservaciones] = useState("")
  const [loadingServicios, setLoadingServicios] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [itemGrupoId, setItemGrupoId] = useState(null)
  const [idPaqueteEdicion, setIdPaqueteEdicion] = useState(null)
  const [destinoOriginalId, setDestinoOriginalId] = useState(null)
  const [baseItemData, setBaseItemData] = useState(null)
  const [editDataLoaded, setEditDataLoaded] = useState(false)
  const [bomMontoByKey, setBomMontoByKey] = useState({})
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([])
  const fileInputRef = useRef(null)

  const nochesCalculoAutomaticoActivo = !isEditMode && parseBooleanParametro(nochesCalculoAutomaticoInicial)

  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState("")
  const [alertSeverity, setAlertSeverity] = useState("success")

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message)
    setAlertSeverity(severity)
    setAlertOpen(true)
  }

  const formatMontoServicio = (monto) => {
    const montoNum = Number(monto)
    if (!Number.isFinite(montoNum)) return "$ 0"
    return `$ ${formatInputWithSeparators(montoNum, localeConfig.locale)}`
  }

  useEffect(() => {
    if (typeof onBaseItemChange !== "function") return

    const nombre = codigoGrupo?.trim()
    if (!nombre) {
      onBaseItemChange(null)
      return
    }

    onBaseItemChange({
      idItem: isEditMode ? itemGrupoId : null,
      codigo: nombre,
      descripcion: nombre,
      atributos: atributosForm.map((attr) => ({
        id: attr.id,
        valor: attr.valor || "",
      })),
    })
  }, [onBaseItemChange, codigoGrupo, atributosForm, isEditMode, itemGrupoId])

  useEffect(() => {
    return () => {
      if (typeof onBaseItemChange === "function") {
        onBaseItemChange(null)
      }
    }
  }, [onBaseItemChange])

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getUbicacion()
        await getItemAtributoNoEliminados()
      } catch (error) {
        console.error("Error al cargar datos para crear grupo paquete:", error)
        showAlert("Error al cargar datos iniciales", "error")
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (!itemAtributoNoEliminados || itemAtributoNoEliminados.length === 0) {
      setAtributosForm([])
      return
    }

    setAtributosForm((prev) => {
      const valoresPrevios = new Map((prev || []).map((attr) => [attr.id, attr.valor]))
      return itemAtributoNoEliminados.map((attr) => ({
        id: attr.id,
        descripcion: attr.descripcion,
        valor: valoresPrevios.get(attr.id) || "",
      }))
    })
  }, [itemAtributoNoEliminados])

  useEffect(() => {
    if (!isEditMode) return
    setGrupoConfirmado(true)
  }, [isEditMode])

  useEffect(() => {
    if (!isEditMode) return
    setEditDataLoaded(false)
  }, [isEditMode, paqueteInicial?.idPaquete, paqueteInicial?.id])

  useEffect(() => {
    if (!isEditMode || editDataLoaded === true) return
    if (!paqueteInicial) return
    if (!itemAtributoNoEliminados || itemAtributoNoEliminados.length === 0) return
    if (!ubicaciones || ubicaciones.length === 0) return

    const hydrateEditData = async () => {
      try {
        const idItem = Number(paqueteInicial?.idProductoTerminado || paqueteInicial?.idItem || 0)
        const idPaquete = Number(paqueteInicial?.idPaquete || paqueteInicial?.id || 0)
        const destinoId = Number(paqueteInicial?.destinoId || paqueteInicial?.destino || 0)

        if (!Number.isFinite(idItem) || idItem <= 0) {
          showAlert("No se pudo identificar el grupo a editar", "error")
          return
        }

        setItemGrupoId(idItem)
        setIdPaqueteEdicion(Number.isFinite(idPaquete) && idPaquete > 0 ? idPaquete : null)
        setDestinoOriginalId(Number.isFinite(destinoId) && destinoId > 0 ? destinoId : null)

        const itemData = await getItemById(idItem)
        setBaseItemData(itemData || null)

        const nombreGrupo =
          itemData?.codigo ||
          itemData?.descripcion ||
          paqueteInicial?.codigoProductoTerminado ||
          paqueteInicial?.descripcionProductoTerminado ||
          ""
        setCodigoGrupo(nombreGrupo)

        setAtributosForm(
          itemAtributoNoEliminados.map((attr) => ({
            id: attr.id,
            descripcion: attr.descripcion,
            valor: itemData?.[`itemDatoAtributo${attr.id}`] || "",
          }))
        )

        const fechaInicioBase = parseDateValue(paqueteInicial?.fechaInicioRaw || paqueteInicial?.fechaInicio)
        const fechaFinBase = parseDateValue(paqueteInicial?.fechaFinRaw || paqueteInicial?.fechaFin)
        if (fechaInicioBase) setFechaInicio(fechaInicioBase)
        setFechaFin(fechaFinBase)

        const nochesValor = paqueteInicial?.noches
        setNoches(
          nochesValor === null || nochesValor === undefined || nochesValor === ""
            ? ""
            : String(nochesValor)
        )

        const destino = (ubicaciones || []).find((ubi) => Number(ubi.id) === destinoId) || null
        setDestinoSeleccionado(destino)

        const butacasValor = paqueteInicial?.butacas
        setButacas(
          butacasValor === null || butacasValor === undefined || butacasValor === ""
            ? ""
            : String(Number(butacasValor))
        )

        const montoBase = Number(paqueteInicial?.monto ?? paqueteInicial?.precioPaquete ?? 0)
        setPrecioPaquete(montoBase)
        setPrecioPaqueteDisplay(
          montoBase === 0 ? "" : formatInputWithSeparators(montoBase, localeConfig.locale)
        )
        setObservaciones(paqueteInicial?.observaciones || "")

        setArchivosAdjuntos(parseArchivosAdjuntoDesdeItem(itemData?.archivosAdjunto))

        const [destinoResponse, serviciosPaqueteRaw] = await Promise.all([
          destinoId ? getItemAndItemUbicacionServicios(1, 1000, destinoId) : Promise.resolve({ data: [] }),
          getBomByIdItem(idItem, destinoId || null, idPaquete || null),
        ])

        const serviciosDestino = uniqueAndSortServicios(destinoResponse?.data || [])
        const serviciosPaquete = uniqueAndSortServicios(serviciosPaqueteRaw || [])
        const bomMontos = {}
        serviciosPaquete.forEach((servicio) => {
          const montoBom = Number(servicio.montoUnitario)
          if (servicio?.key && Number.isFinite(montoBom)) {
            bomMontos[servicio.key] = montoBom
          }
        })

        setServiciosDisponibles(serviciosDestino)
        setServiciosAsignados(serviciosPaquete)
        setBomMontoByKey(bomMontos)

        setEditDataLoaded(true)
      } catch (error) {
        console.error("Error al cargar datos de ediciÃ³n del paquete:", error)
        showAlert("No se pudieron cargar los datos del paquete a editar", "error")
      }
    }

    hydrateEditData()
  }, [
    isEditMode,
    editDataLoaded,
    paqueteInicial,
    itemAtributoNoEliminados,
    ubicaciones,
    getItemById,
    getBomByIdItem,
    getItemAndItemUbicacionServicios,
    localeConfig.locale,
  ])

  useEffect(() => {
    if (!nochesCalculoAutomaticoActivo) return
    if (!fechaInicio) return

    const nochesNum = parseInt(noches, 10)
    if (!Number.isFinite(nochesNum) || nochesNum < 0) {
      setFechaFin(null)
      return
    }

    const nuevaFechaFin = new Date(fechaInicio)
    nuevaFechaFin.setDate(nuevaFechaFin.getDate() + nochesNum)
    setFechaFin(nuevaFechaFin)
  }, [nochesCalculoAutomaticoActivo, fechaInicio, noches])

  const destinosDisponibles = useMemo(() => {
    const destinos = (ubicaciones || []).filter((ubicacion) => {
      if (ubicacion?.esDestino === undefined || ubicacion?.esDestino === null) return true
      return Boolean(ubicacion.esDestino)
    })
    return destinos
  }, [ubicaciones])

  const headerColumns = useMemo(() => {
    const totalCampos = 1 + atributosForm.length
    return Math.max(1, Math.min(5, totalCampos))
  }, [atributosForm.length])

  const handleAtributoChange = (id, value) => {
    setAtributosForm((prev) => prev.map((attr) => (attr.id === id ? { ...attr, valor: value } : attr)))
  }

  const handleConfirmarGrupo = () => {
    if (!codigoGrupo || codigoGrupo.trim().length < 3) {
      showAlert("El nombre del grupo debe tener al menos 3 caracteres", "warning")
      return
    }

    if (!fechaInicio) {
      showAlert("La fecha inicio es obligatoria", "warning")
      return
    }

    setGrupoConfirmado(true)
    showAlert("Grupo confirmado. CompletÃ¡ destino, servicios y observaciones.", "info")
  }

  const resetServicios = () => {
    setServiciosDisponibles([])
    setServiciosAsignados([])
  }

  const handleArchivosSeleccionados = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    if (files.length === 0) return

    const slot = MAX_ARCHIVOS_ADJUNTO - archivosAdjuntos.length
    if (slot <= 0) {
      showAlert(`Máximo ${MAX_ARCHIVOS_ADJUNTO} archivos`, "warning")
      return
    }

    const toAdd = files.slice(0, slot)
    if (files.length > slot) {
      showAlert(`Solo se agregaron ${slot} archivo(s) (máximo ${MAX_ARCHIVOS_ADJUNTO} en total)`, "info")
    }

    const nuevos = []
    for (const file of toAdd) {
      if (file.size > MAX_BYTES_ARCHIVO) {
        showAlert(`"${file.name}" supera el límite de 2 MB`, "warning")
        continue
      }
      try {
        const dataUrl = await readFileAsBase64DataUrl(file)
        nuevos.push({
          id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          nombre: file.name,
          mimeType: file.type || "application/octet-stream",
          data: dataUrl,
        })
      } catch {
        showAlert(`No se pudo leer ${file.name}`, "error")
      }
    }
    if (nuevos.length) {
      setArchivosAdjuntos((prev) => [...prev, ...nuevos])
    }
  }

  const quitarArchivoAdjunto = (id) => {
    setArchivosAdjuntos((prev) => prev.filter((a) => a.id !== id))
  }

  const quitarTodosArchivosAdjuntos = () => {
    setArchivosAdjuntos([])
  }

  const hrefDescargaArchivo = (adj) => {
    const raw = typeof adj.data === "string" ? adj.data : ""
    if (raw.startsWith("data:")) return raw
    const mime = adj.mimeType || "application/octet-stream"
    return `data:${mime};base64,${raw}`
  }

  const cargarServiciosDestino = async (destinoId) => {
    if (!destinoId) {
      resetServicios()
      return
    }

    setLoadingServicios(true)
    try {
      const response = await getItemAndItemUbicacionServicios(1, 1000, destinoId)
      const serviciosDestino = uniqueAndSortServicios(response?.data || [])

      setServiciosDisponibles(serviciosDestino)
      setServiciosAsignados([])
    } catch (error) {
      console.error("Error al cargar servicios por destino:", error)
      showAlert("No se pudieron cargar los servicios del destino", "error")
      resetServicios()
    } finally {
      setLoadingServicios(false)
    }
  }

  const handleDestinoChange = async (newValue) => {
    setDestinoSeleccionado(newValue)
    await cargarServiciosDestino(newValue?.id || null)
  }

  const clearForm = () => {
    setCodigoGrupo("")
    setAtributosForm((prev) => prev.map((attr) => ({ ...attr, valor: "" })))
    setFechaInicio(new Date())
    setNoches("0")
    setFechaFin(null)
    setGrupoConfirmado(false)
    setDestinoSeleccionado(null)
    setButacas("")
    setPrecioPaquete("")
    setPrecioPaqueteDisplay("")
    setObservaciones("")
    setBomMontoByKey({})
    setArchivosAdjuntos([])
    resetServicios()
  }

  const handleCrearPaquete = async () => {
    if (!grupoConfirmado) {
      showAlert("Primero confirmÃ¡ el grupo", "warning")
      return
    }

    if (!destinoSeleccionado?.id) {
      showAlert("Debe seleccionar un destino", "warning")
      return
    }

    if (!fechaInicio) {
      showAlert("La fecha inicio es obligatoria", "warning")
      return
    }

    if (fechaFin && fechaInicio && fechaFinEsAnteriorAlInicio(fechaFin, fechaInicio)) {
      showAlert("La fecha fin no puede ser anterior al día de la fecha inicio", "warning")
      return
    }

    const butacasNum = butacas === "" ? 0 : Number(butacas)
    if (!Number.isFinite(butacasNum) || butacasNum < 0) {
      showAlert("Butacas debe ser un nÃºmero vÃ¡lido", "warning")
      return
    }

    const montoNum = precioPaquete === "" ? 0 : Number(precioPaquete)
    if (!Number.isFinite(montoNum) || montoNum < 0) {
      showAlert("Precio del paquete debe ser un nÃºmero vÃ¡lido", "warning")
      return
    }

    const nochesNum = noches === "" ? null : parseInt(noches, 10)
    if (noches !== "" && (!Number.isFinite(nochesNum) || nochesNum < 0)) {
      showAlert("Noches debe ser un entero mayor o igual a 0", "warning")
      return
    }

    const adjuntosParaApi = archivosAdjuntos.map(({ nombre, mimeType, data }) => {
      let d = data
      if (typeof d === "string" && d.startsWith("data:")) {
        const i = d.indexOf("base64,")
        if (i !== -1) d = d.slice(i + 7)
      }
      return { nombre, mimeType, data: d }
    })

    setIsSubmitting(true)
    try {
      if (!isEditMode) {
        const payload = {
          codigo: codigoGrupo.trim(),
          descripcion: codigoGrupo.trim(),
          atributos: atributosForm.map((attr) => ({
            id: attr.id,
            valor: attr.valor || "",
          })),
          fechaInicio: fechaInicio.toISOString(),
          fechaFin: fechaFin ? fechaFin.toISOString() : null,
          noches: nochesNum,
          destino: destinoSeleccionado.id,
          butacas: butacasNum,
          monto: montoNum,
          servicios: serviciosAsignados.map((servicio) => ({
            idItem: servicio.idItem,
            idEntidad: servicio.idEntidad,
            montoUnitario: servicio.montoUnitario || 0,
          })),
          observaciones: observaciones?.trim() || null,
        }

        if (adjuntosParaApi.length > 0) {
          payload.archivosAdjunto = adjuntosParaApi
        }

        await crearGrupoPaqueteApi(payload)
        showAlert("Paquete creado correctamente", "success")
        clearForm()
      } else {
        if (!itemGrupoId || !idPaqueteEdicion) {
          showAlert("No se pudo identificar el paquete a actualizar", "error")
          return
        }

        await updateItem(
          itemGrupoId,
          codigoGrupo.trim(),
          codigoGrupo.trim(),
          baseItemData?.codigoScanner || null,
          atributosForm.map((attr) => ({
            id: attr.id,
            valor: attr.valor || "",
          })),
          baseItemData?.eliminado ?? false,
          baseItemData?.pluBalanza ?? null,
          baseItemData?.descripcionServicio ?? null,
          baseItemData?.esActividad ?? false,
          adjuntosParaApi.length > 0 ? adjuntosParaApi : null
        )

        await updateItemUbicaciones(itemGrupoId, [
          {
            idUbicacion: destinoSeleccionado.id,
            idUbicacionOriginal:
              Number.isFinite(Number(destinoOriginalId)) && Number(destinoOriginalId) > 0
                ? Number(destinoOriginalId)
                : destinoSeleccionado.id,
            inventario: butacasNum,
            stockMinimo: 0,
          },
        ])

        const ingredientes = serviciosAsignados.map((servicio) => ({
          idItem: servicio.idItem,
          idEntidad: servicio.idEntidad,
          cantidad: 1,
          monto: servicio.montoUnitario || 0,
        }))

        await crearRecetaApi(
          itemGrupoId,
          ingredientes,
          true,
          destinoSeleccionado.id,
          fechaInicio.toISOString(),
          fechaFin ? fechaFin.toISOString() : null,
          nochesNum,
          idPaqueteEdicion,
          codigoGrupo.trim(),
          montoNum,
          observaciones?.trim() || null
        )

        showAlert("Paquete actualizado correctamente", "success")
      }

      if (onClose) {
        setTimeout(() => onClose(), 900)
      }
    } catch (error) {
      const backendMessage = error?.response?.data?.message
      showAlert(backendMessage || "Error al guardar paquete", "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 600, color: "rgb(244, 147, 14)" }}>
              {isEditMode ? "Editar Grupo" : "Crear Nuevo Grupo"}
            </Typography>
          }
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        />
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: {
                xs: "1fr",
                md: `repeat(${headerColumns}, minmax(0, 1fr))`,
              },
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Nombre"
              value={codigoGrupo}
              onChange={(e) => setCodigoGrupo(e.target.value)}
              error={codigoGrupo.trim().length > 0 && codigoGrupo.trim().length < 3}
              helperText={
                codigoGrupo.trim().length > 0 && codigoGrupo.trim().length < 3
                  ? "MÃ­nimo 3 caracteres"
                  : ""
              }
            />

            {atributosForm.map((attr) => (
              <TextField
                key={attr.id}
                fullWidth
                size="small"
                label={attr.descripcion}
                value={attr.valor}
                onChange={(e) => handleAtributoChange(attr.id, e.target.value)}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={1.25} alignItems="center">
              <Grid item xs={12} md={3}>
                <DatePicker
                  value={fechaInicio ? dayjs(fechaInicio) : null}
                  onChange={(newValue) => {
                    if (!newValue) {
                      setFechaInicio(null)
                      return
                    }
                    const r = newValue.toDate()
                    setFechaInicio(new Date(r.getFullYear(), r.getMonth(), r.getDate()))
                  }}
                  format="DD/MM/YYYY"
                  slots={{ openPickerIcon: EventIcon }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      label: "Fecha Inicio",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Noches"
                  type="number"
                  value={noches}
                  disabled={false}
                  onChange={(e) => {
                    const valor = e.target.value
                    setNoches(valor)

                    if (!nochesCalculoAutomaticoActivo) return

                    if (fechaInicio && valor !== "" && !Number.isNaN(parseInt(valor, 10))) {
                      const nochesNum = parseInt(valor, 10)
                      if (nochesNum >= 0) {
                        const nuevaFechaFin = new Date(fechaInicio)
                        nuevaFechaFin.setDate(nuevaFechaFin.getDate() + nochesNum)
                        setFechaFin(nuevaFechaFin)
                      }
                    } else if (valor === "") {
                      setFechaFin(null)
                    }
                  }}
                  inputProps={{ min: 0, step: 1 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <DatePicker
                  disabled={false}
                  value={fechaFin ? dayjs(fechaFin) : null}
                  onChange={(newValue) => {
                    if (!newValue) {
                      setFechaFin(null)
                      if (nochesCalculoAutomaticoActivo) {
                        setNoches("")
                      }
                      return
                    }
                    const nuevaFechaFin = newValue.toDate()
                    setFechaFin(nuevaFechaFin)

                    if (nochesCalculoAutomaticoActivo && fechaInicio) {
                      const diferencia = Math.ceil((nuevaFechaFin - fechaInicio) / (1000 * 60 * 60 * 24))
                      setNoches(diferencia >= 0 ? String(diferencia) : "")
                    }
                  }}
                  format="DD/MM/YYYY"
                  minDate={fechaInicio ? dayjs(fechaInicio) : undefined}
                  slots={{ openPickerIcon: EventIcon }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      label: "Fecha Fin",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" } }}>
                  <Button
                    variant="contained"
                    onClick={handleConfirmarGrupo}
                    fullWidth
                    disabled={isEditMode}
                    sx={{
                      maxWidth: { xs: "100%", md: 230 },
                      borderRadius: 1.5,
                      textTransform: "none",
                      backgroundColor: "#5b5b5f",
                    }}
                  >
                    {isEditMode ? "Grupo en ediciÃ³n" : "Confirmar Grupo"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {(grupoConfirmado || isEditMode) && (
        <>
          <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
            <CardHeader
              avatar={<PlaceIcon sx={{ color: "rgb(244, 147, 14)" }} />}
              title={<Typography variant="h6">Destino</Typography>}
              subheader="Un grupo solo puede tener un destino"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
            <CardContent>
              <Grid container spacing={1.25}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    size="small"
                    options={destinosDisponibles}
                    getOptionLabel={(option) => option.descripcion || ""}
                    value={destinoSeleccionado}
                    onChange={(_, newValue) => handleDestinoChange(newValue)}
                    renderInput={(params) => <TextField {...params} label="Destino" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Butacas"
                    type="number"
                    value={butacas}
                    onChange={(e) => setButacas(e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FormatListNumberedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Monto del grupo"
                    value={precioPaqueteDisplay}
                    onChange={(e) => {
                      const inputValue = e.target.value
                      const parsed = parseLocalizedNumber(inputValue, localeConfig.locale)

                      if (inputValue === "") {
                        setPrecioPaquete("")
                        setPrecioPaqueteDisplay("")
                        return
                      }

                      if (parsed === null) return

                      setPrecioPaquete(parsed)
                      setPrecioPaqueteDisplay(formatInputWithSeparators(parsed, localeConfig.locale))
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#2e7d32",
                        fontWeight: 700,
                      },
                      "& .MuiInputAdornment-root": {
                        color: "#2e7d32",
                        fontWeight: 700,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
            <CardHeader
              avatar={<InventoryIcon sx={{ color: "rgb(244, 147, 14)" }} />}
              title={<Typography variant="h6">Servicios</Typography>}
              subheader="Click en un servicio para moverlo"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
            <CardContent sx={{ py: 1.5 }}>
              <ServiciosList
                serviciosDisponibles={serviciosDisponibles}
                serviciosAsignados={serviciosAsignados}
                onServiciosAsignadosChange={setServiciosAsignados}
                loading={loadingServicios}
                bomMontoByKey={bomMontoByKey}
                formatMontoServicio={formatMontoServicio}
              />
            </CardContent>
          </Card>

          <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
            <CardHeader
              avatar={<NotesIcon sx={{ color: "rgb(244, 147, 14)" }} />}
              title={<Typography variant="h6">Observaciones</Typography>}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
            <CardContent>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Observaciones del paquete"
              />
            </CardContent>
          </Card>

          <Card elevation={3} sx={{ mb: 2, borderRadius: 2 }}>
            <CardHeader
              avatar={<AttachFileIcon sx={{ color: "rgb(244, 147, 14)" }} />}
              title={<Typography variant="h6">Archivos adjuntos</Typography>}
              subheader={
                isEditMode
                  ? `Hasta ${MAX_ARCHIVOS_ADJUNTO} archivos, máx. 2 MB c/u. Podés agregar nuevos o quitar los existentes; al guardar se reemplaza todo lo guardado antes.`
                  : `Hasta ${MAX_ARCHIVOS_ADJUNTO} archivos, máx. 2 MB c/u (se guardan en el grupo).`
              }
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            />
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleArchivosSeleccionados}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={archivosAdjuntos.length >= MAX_ARCHIVOS_ADJUNTO || isSubmitting}
                  sx={{ textTransform: "none" }}
                >
                  Agregar archivos
                </Button>
                {archivosAdjuntos.length > 0 ? (
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={quitarTodosArchivosAdjuntos}
                    disabled={isSubmitting}
                    sx={{ textTransform: "none" }}
                  >
                    Quitar todos
                  </Button>
                ) : null}
              </Box>
              {archivosAdjuntos.length > 0 ? (
                <List dense disablePadding>
                  {archivosAdjuntos.map((adj) => (
                    <ListItem
                      key={adj.id}
                      sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 0.5 }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => quitarArchivoAdjunto(adj.id)}
                          aria-label="Quitar archivo"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={adj.nombre}
                        secondary={
                          <Link
                            href={hrefDescargaArchivo(adj)}
                            download={adj.nombre}
                            underline="hover"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Ver / descargar
                          </Link>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Sin archivos adjuntos.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3, paddingBottom: "10px" }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleCrearPaquete}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <AddTaskIcon />}
              sx={{ borderRadius: 2, textTransform: "none", px: 5 }}
            >
              {isSubmitting ? (isEditMode ? "Guardando..." : "Creando...") : isEditMode ? "Guardar Cambios" : "Crear Grupo"}
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={alertOpen}
        autoHideDuration={4500}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
